import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLAnchorElement} [viewMore]
 *
 * @extends {Component<Refs>}
 */
class FeaturedCollectionTabsComponent extends Component {
  #autoRotateTimer;

  #autoRotateStoppedByUser = false;

  #isInViewport = true;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      this.#isInViewport = entries[0]?.isIntersecting ?? true;

      if (this.#isInViewport) {
        this.#queueAutoRotate();
      } else {
        this.#clearAutoRotate();
      }
    });
    this.#intersectionObserver.observe(this);

    const initialIndex = Number(this.dataset.initialIndex || 0);
    this.activateTab(initialIndex, { scrollBehavior: 'auto' });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#intersectionObserver?.disconnect();
    this.#clearAutoRotate();
  }

  get tabs() {
    return this.refs.tabs || [];
  }

  get panels() {
    return this.refs.panels || [];
  }

  get autoRotateEnabled() {
    return this.dataset.autoplayEnabled === 'true';
  }

  get autoRotateDelay() {
    const delay = Number(this.dataset.autoplayDelay || 0);

    return Number.isFinite(delay) && delay >= 1500 ? delay : 4000;
  }

  /**
   * @param {number} index
   * @param {Event | { stopAutoRotate?: boolean, scrollBehavior?: ScrollBehavior } | undefined} [trigger]
   */
  activateTab(index, trigger) {
    const tabs = this.tabs;
    const panels = this.panels;

    if (!tabs.length || !panels.length) return;

    const options = this.#resolveActivationOptions(trigger);
    const normalizedIndex = Math.max(0, Math.min(index, tabs.length - 1));
    const activeTab = tabs[normalizedIndex];
    const activePanel = panels[normalizedIndex];

    if (!(activeTab instanceof HTMLElement) || !(activePanel instanceof HTMLElement)) return;

    tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === normalizedIndex;

      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.toggleAttribute('data-active', isActive);
    });

    panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== normalizedIndex;
    });

    const viewMore = this.refs.viewMore;
    if (viewMore instanceof HTMLAnchorElement) {
      const nextHref = activeTab.dataset.collectionUrl || '#';
      viewMore.href = nextHref;
      viewMore.toggleAttribute('aria-disabled', nextHref === '#');
      viewMore.classList.toggle('is-disabled', nextHref === '#');
    }

    this.#scrollTabIntoView(activeTab, options.scrollBehavior);

    if (options.stopAutoRotate) {
      this.#stopAutoRotate();
      return;
    }

    this.#queueAutoRotate();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const tabs = this.tabs;
    const currentTab = event.target instanceof HTMLElement ? event.target.closest('[role="tab"]') : null;

    if (!(currentTab instanceof HTMLElement) || !tabs.length) return;

    const currentIndex = tabs.indexOf(currentTab);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activateTab(nextIndex, { stopAutoRotate: true });
    tabs[nextIndex]?.focus();
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;

    const tab = event.target.matches('[data-tab-index]')
      ? event.target
      : event.target.querySelector('[data-tab-index]');

    if (!(tab instanceof HTMLElement)) return;

    const index = Number(tab.dataset.tabIndex);
    if (Number.isNaN(index)) return;

    this.activateTab(index, { stopAutoRotate: true });
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#clearAutoRotate();
      return;
    }

    this.#queueAutoRotate();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.#openCartDrawer();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[featured-tabs] Cart drawer open failed:', error);
      });
  };

  /**
   * @param {Event | { stopAutoRotate?: boolean, scrollBehavior?: ScrollBehavior } | undefined} trigger
   */
  #resolveActivationOptions(trigger) {
    if (trigger instanceof Event) {
      return {
        stopAutoRotate: trigger.type === 'click',
        scrollBehavior: 'smooth',
      };
    }

    return {
      stopAutoRotate: Boolean(trigger?.stopAutoRotate),
      scrollBehavior: trigger?.scrollBehavior || 'smooth',
    };
  }

  #queueAutoRotate() {
    this.#clearAutoRotate();

    if (!this.#shouldAutoRotate()) return;

    this.#autoRotateTimer = window.setTimeout(() => {
      this.#advanceToNextTab();
    }, this.autoRotateDelay);
  }

  #advanceToNextTab() {
    if (!this.#shouldAutoRotate()) return;

    const tabs = this.tabs;
    const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % tabs.length;

    this.activateTab(nextIndex, { scrollBehavior: 'smooth' });
  }

  #stopAutoRotate() {
    this.#autoRotateStoppedByUser = true;
    this.#clearAutoRotate();
  }

  #clearAutoRotate() {
    if (typeof this.#autoRotateTimer === 'number') {
      window.clearTimeout(this.#autoRotateTimer);
    }

    this.#autoRotateTimer = undefined;
  }

  #shouldAutoRotate() {
    return (
      this.autoRotateEnabled &&
      !this.#autoRotateStoppedByUser &&
      !document.hidden &&
      this.#isInViewport &&
      this.tabs.length > 1 &&
      this.panels.length > 1
    );
  }

  /**
   * Scrolls a tab into view within the horizontally-scrolling tab strip only.
   * Deliberately avoids `Element.scrollIntoView`, which also considers the
   * window/page as a scroll container and can yank the whole viewport back
   * to this section (e.g. while the auto-rotate timer fires in the
   * background and the user has scrolled elsewhere on the page).
   *
   * @param {HTMLElement} tab
   * @param {string} [behavior]
   */
  #scrollTabIntoView(tab, behavior = 'smooth') {
    const container = tab.closest('.featured-tabs__tab-list');
    if (!(container instanceof HTMLElement)) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const scrollBehavior = /** @type {ScrollBehavior} */ (behavior);

    if (tabRect.left < containerRect.left) {
      container.scrollBy({ left: tabRect.left - containerRect.left - 16, behavior: scrollBehavior });
    } else if (tabRect.right > containerRect.right) {
      container.scrollBy({ left: tabRect.right - containerRect.right + 16, behavior: scrollBehavior });
    }
  }

  #openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');

    if (drawer instanceof HTMLElement && typeof drawer.open === 'function') {
      if (!drawer.hasAttribute('open')) {
        drawer.open();
      }

      return;
    }

    window.location.href = window.Theme?.routes?.cart_url || '/cart';
  }
}

if (!customElements.get('featured-collection-tabs-component')) {
  customElements.define('featured-collection-tabs-component', FeaturedCollectionTabsComponent);
}
